import { getDisplayText } from '../searchUtils';
import { ResultMetadata, SearxngResponse, SearchResult } from '../types';
import { ImageResultCard, TextResultCard, VideoResultCard } from './ResultCards';

type ResultsSectionProps = {
  data: SearxngResponse | null;
  error: string;
  isImageSearch: boolean;
  isLoading: boolean;
  isVideoSearch: boolean;
  metadataByUrl: Record<string, ResultMetadata>;
  page: number;
  resultCount: string;
  results: SearchResult[];
  shareStatus: string;
  submittedQuery: string;
  onCorrectionSearch: (query: string) => void;
  onNextPage: () => void;
  onPreviousPage: () => void;
  onShareSearch: () => void;
  onSuggestionSearch: (query: string) => void;
};

export function ResultsSection({
  data,
  error,
  isImageSearch,
  isLoading,
  isVideoSearch,
  metadataByUrl,
  page,
  resultCount,
  results,
  shareStatus,
  submittedQuery,
  onCorrectionSearch,
  onNextPage,
  onPreviousPage,
  onShareSearch,
  onSuggestionSearch,
}: ResultsSectionProps) {
  return (
    <section className="results-section" aria-live="polite">
      <div className="results-meta">
        <div>
          <span>{isLoading ? 'Searching...' : resultCount}</span>
          {data?.query && <span> for "{data.query}"</span>}
        </div>
        <button className="share-button" type="button" onClick={onShareSearch}>
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <path d="M4 12v7a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1v-7" />
            <path d="m16 6-4-4-4 4" />
            <path d="M12 2v14" />
          </svg>
          <span>{shareStatus || 'Share'}</span>
        </button>
      </div>

      {error && <div className="status-message error">{error}</div>}

      {!error && data?.answers?.map((answer, index) => {
        const answerText = getDisplayText(answer);

        return answerText ? (
          <article className="answer-card" key={`${answerText}-${index}`}>
            {answerText}
          </article>
        ) : null;
      })}

      {!error && data?.corrections?.map((correction, index) => {
        const correctionText = getDisplayText(correction);

        return correctionText ? (
          <button
            className="suggestion"
            type="button"
            key={`${correctionText}-${index}`}
            onClick={() => onCorrectionSearch(correctionText)}
          >
            Search instead for {correctionText}
          </button>
        ) : null;
      })}

      {!error && isLoading && (
        <div className="loading-list" aria-hidden="true">
          <span />
          <span />
          <span />
        </div>
      )}

      {!error && !isLoading && isImageSearch && (
        <div className="image-grid">
          {results.map((result, index) => (
            <ImageResultCard
              key={`${result.url}-${index}`}
              result={result}
              submittedQuery={submittedQuery}
            />
          ))}
        </div>
      )}

      {!error && !isLoading && isVideoSearch && (
        <div className="video-list">
          {results.map((result, index) => (
            <VideoResultCard
              key={`${result.url}-${index}`}
              result={result}
              submittedQuery={submittedQuery}
            />
          ))}
        </div>
      )}

      {!error && !isLoading && !isImageSearch && !isVideoSearch && (
        <div className="result-list">
          {results.map((result, index) => (
            <TextResultCard
              key={`${result.url}-${index}`}
              result={result}
              metadata={metadataByUrl[result.url || '']}
            />
          ))}
        </div>
      )}

      {!error && !isLoading && Boolean(data?.suggestions?.length) && (
        <div className="suggestions" aria-label="Related searches">
          {data?.suggestions?.map((suggestion, index) => {
            const suggestionText = getDisplayText(suggestion);

            return suggestionText ? (
              <button type="button" key={`${suggestionText}-${index}`} onClick={() => onSuggestionSearch(suggestionText)}>
                {suggestionText}
              </button>
            ) : null;
          })}
        </div>
      )}

      {!error && !isLoading && results.length > 0 && (
        <nav className="pagination" aria-label="Search results pages">
          <button type="button" disabled={page === 1} onClick={onPreviousPage}>
            Previous
          </button>
          <span>Page {page}</span>
          <button type="button" onClick={onNextPage}>
            Next
          </button>
        </nav>
      )}
    </section>
  );
}
